import React from 'react';
import { Box, Typography } from '@availity/element';
import Button from './Button';
import checkIcon from '../../../static/assets/icons/check.svg';

const iconOrangeFilter = 'brightness(0) saturate(100%) invert(45%) sepia(98%) saturate(2618%) hue-rotate(0deg) brightness(102%) contrast(101%)';
const iconCheckFilter = 'brightness(0) saturate(100%) invert(27%) sepia(89%) saturate(1447%) hue-rotate(8deg) brightness(94%) contrast(92%)';

export const ValueCard = ({ badge, title, description, theme = 'dark' }: { badge: string; title: string; description: string; theme?: 'light' | 'dark' }): JSX.Element => (
  <Box sx={{ bgcolor: theme === 'light' ? '#F5F6F8' : '#191F31', p: 4, borderRadius: 3}}>
    <Box sx={{ 
      bgcolor: '#BF4D17', 
      color: '#fff', 
      width: 187, 
      height: 25, 
      borderRadius: '100px', 
      pt: 1, 
      pr: 1.5, 
      pb: 1, 
      pl: 1.5, 
      fontSize: '12px', 
      fontWeight: 700, 
      fontFamily: 'Lato',
      lineHeight: '100%',
      letterSpacing: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 2.5 
    }}>
      {badge}
    </Box>
    <Typography sx={{ fontSize: '24px', fontWeight: 600, mb: 2, color: theme === 'light' ? '#000C30' : '#FFFFFF', lineHeight: '32px', fontFamily: 'Montserrat', width: 240, height: 64 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: '18px', lineHeight: '26px', color: theme === 'light' ? '#000C30' : '#FFFFFF', m: 0, fontFamily: 'Lato', fontWeight: 400, letterSpacing: 0 }}>
      {description}
    </Typography>
  </Box>
);

export const InfoCard = ({ icon, title, description, theme = 'dark' }: { icon: string; title: string; description: string; theme?: 'light' | 'dark' }): JSX.Element => (
  <Box sx={{ bgcolor: theme === 'light' ? '#F5F6F8' : '#191F31', width: '100%', height: '100%', p: 3, borderRadius: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box component="img" src={icon} sx={{ width: 24, height: 24, opacity: 1, filter: iconOrangeFilter }} />
    <Typography component="h3" sx={{ fontSize: '20px', fontWeight: 600, color: theme === 'light' ? '#000C30' : '#BBD5EA', lineHeight: '24px', fontFamily: 'Montserrat', letterSpacing: 0 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: '16px', lineHeight: '26px', color: theme === 'light' ? '#000C30' : '#EFF3FF', m: 0, fontFamily: 'Lato', fontWeight: 400, letterSpacing: 0 }}>
      {description}
    </Typography>
  </Box>
);

export const ApiCard = ({ title, description, items, buttonText, onButtonClick, theme = 'dark' }: { title: string; description: string; items: string[]; buttonText: string; onButtonClick?: () => void; theme?: 'light' | 'dark' }): JSX.Element => (
  <Box sx={{ bgcolor: theme === 'light' ? '#F5F6F8' : '#191F31', flex: 1, p: 3, borderRadius: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
    <Box>
      <Typography component="h3" sx={{ fontSize: '24px', fontWeight: 600, mb: 2, color: theme === 'light' ? '#000C30' : '#FFFFFF', lineHeight: '32px', fontFamily: 'Montserrat', letterSpacing: 0 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '18px', lineHeight: '26px', mb: 2, color: theme === 'light' ? '#000C30' : '#FFFFFF', fontFamily: 'Lato', fontWeight: 400, letterSpacing: 0 }}>
        {description}
      </Typography>
      <Box component="ul" sx={{ listStyle: 'disc', pl: 2.5, color: theme === 'light' ? '#000C30' : '#FFFFFF', fontSize: '18px', lineHeight: '26px', fontFamily: 'Lato', fontWeight: 400, letterSpacing: 0 }}>
        {items.map((item) => (
          <Box key={item} component="li" sx={{ mb: 1 }}>{item}</Box>
        ))}
      </Box>
    </Box>
    <Button variant="navbar" aria-label={`${buttonText} - ${title}`} sx={{ borderRadius: '24px', mt: 2, alignSelf: 'flex-start', cursor: onButtonClick ? 'pointer' : 'default', pointerEvents: onButtonClick ? 'auto' : 'none' }} onClick={onButtonClick}>
      {buttonText}
    </Button>
  </Box>
);

export const QuickstartStep = ({ number, title, description, theme = 'dark' }: { number: string; title: string; description: string; theme?: 'light' | 'dark' }): JSX.Element => (
  <Box sx={{ display: 'flex', gap: 3 }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '100px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: '#191F31' }}>
      <Typography sx={{ fontSize: '16.95px', fontWeight: 700, color: '#FFFFFF', fontFamily: 'Lato', lineHeight: '24.01px', letterSpacing: 0 }}>{number}</Typography>
    </Box>
    <Box>
      <Typography component="h3" sx={{ fontSize: '20px', fontWeight: 600, mb: 1.5, color: theme === 'light' ? '#000C30' : '#FFFFFF', lineHeight: '24px', fontFamily: 'Montserrat', letterSpacing: 0 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '18px', lineHeight: '26px', color: theme === 'light' ? '#000C30' : '#FFFFFF', m: 0, fontFamily: 'Lato', fontWeight: 400, letterSpacing: 0 }}>
        {description}
      </Typography>
    </Box>
  </Box>
);

export const DemoFlowCard = ({ number, title, description, tags, theme = 'dark' }: { number: string; title: string; description: string; tags: string[]; theme?: 'light' | 'dark' }): JSX.Element => (
  <Box sx={{ bgcolor: theme === 'light' ? '#F5F6F8' : '#191F31', flex: 1, height: 273, p: 3, borderRadius: '8px' }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '100px', padding: '8px 12px', bgcolor: '#2F3444', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
      <Typography sx={{ fontSize: '16.95px', fontWeight: 700, color: '#FFFFFF', fontFamily: 'Lato', lineHeight: '24.01px', letterSpacing: 0 }}>{number}</Typography>
    </Box>
    <Typography component="h3" sx={{ fontSize: '24px', fontWeight: 600, mb: 2, color: theme === 'light' ? '#000C30' : '#FFFFFF', lineHeight: '32px', fontFamily: 'Montserrat', letterSpacing: 0 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: '18px', lineHeight: '26px', mb: 3, color: theme === 'light' ? '#000C30' : '#FFFFFF', fontFamily: 'Lato', fontWeight: 400, letterSpacing: 0 }}>
      {description}
    </Typography>
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {tags.map((tag) => (
        <Box key={tag} component="span" sx={{ bgcolor: '#BF4D17', color: '#FFFFFF', p: '6px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: 700, fontFamily: 'Lato', lineHeight: '100%', letterSpacing: 0 }}>{tag}</Box>
      ))}
    </Box>
  </Box>
);

export const FeatureItem = ({ text, isLast, theme = 'dark' }: { text: string; isLast?: boolean; theme?: 'light' | 'dark' }): JSX.Element => (
  <Box sx={{ display: 'flex', gap: 1.5, mb: isLast ? 0 : 3, pb: isLast ? 0 : 3, borderBottom: isLast ? 'none' : theme === 'light' ? '1px solid #C3CAE2' : '1px solid #334155' }}>
    <Box component="img" src={checkIcon} sx={{ width: 20, height: 20, filter: iconCheckFilter }} />
    <Typography sx={{ fontSize: '18px', lineHeight: '26px', color: theme === 'light' ? '#000C30' : '#EFF3FF', fontFamily: 'Lato', fontWeight: 400, letterSpacing: 0 }}>
      {text}
    </Typography>
  </Box>
);
